import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  TrendingUp, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  Lightbulb,
  Users,
  Target,
  Award,
  Handshake,
  Leaf,
  Sparkles
} from 'lucide-react';

export default function PMEMarchesArticle() {
  return (
    <>
      <SEO 
        title="PME et Marchés Publics | Guide Pratique"
        description="Comment les PME peuvent accéder aux marchés publics : stratégies, astuces et bonnes pratiques pour remporter des contrats."
        path="/blog/pme-marches-publics"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article>
        <header className="bg-gradient-to-b from-teal-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                Stratégie
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                10 janvier 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                9 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              PME et Marchés Publics : Comment Gagner Face aux Grandes Entreprises
            </h1>
            
            <p className="text-xl text-slate-600">
              Stratégies et astuces pour les PME : allotissement, sous-traitance, critères RSE. 
              Transformez votre taille en avantage compétitif.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-slate-700 mb-6">
              Les <strong>PME</strong> représentent 99% des entreprises françaises, mais seulement 
              <strong> 30% des marchés publics</strong> leur sont attribués. Pourtant, de nombreuses 
              opportunités existent pour les petites structures. Voici comment maximiser vos chances.
            </p>

            <Card className="bg-teal-50 border-teal-200 mb-8">
              <CardContent className="p-6">
                <h3 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Bonne nouvelle pour les PME
                </h3>
                <p className="text-teal-800">
                  L'État s'est fixé un objectif de <strong>25% minimum</strong> des marchés attribués aux PME. 
                  De nombreux dispositifs favorisent désormais l'accès des petites entreprises à la commande publique.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Les avantages des PME sur les marchés publics
            </h2>

            <p className="text-slate-600 mb-6">
              Contrairement aux idées reçues, être une PME peut être un <strong>avantage</strong> :
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {[
                { 
                  icon: Target, 
                  title: 'Réactivité', 
                  desc: 'Décisions rapides, moins de bureaucratie interne. Les acheteurs apprécient.',
                  color: 'text-teal-600 bg-teal-100'
                },
                { 
                  icon: Users, 
                  title: 'Proximité', 
                  desc: 'Interlocuteur unique, relation de confiance, connaissance du terrain local.',
                  color: 'text-blue-600 bg-blue-100'
                },
                { 
                  icon: Lightbulb, 
                  title: 'Flexibilité', 
                  desc: 'Adaptation aux demandes spécifiques, solutions sur-mesure.',
                  color: 'text-amber-600 bg-amber-100'
                },
                { 
                  icon: Award, 
                  title: 'Expertise', 
                  desc: 'Spécialisation pointue dans votre domaine, savoir-faire reconnu.',
                  color: 'text-violet-600 bg-violet-100'
                },
              ].map((item, i) => (
                <Card key={`item-${i}`}>
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center mb-4`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Stratégie 1 : Cibler les marchés allotis
            </h2>

            <p className="text-slate-600 mb-6">
              L'<strong>allotissement</strong> est une obligation légale : les acheteurs doivent découper 
              les marchés en lots distincts, sauf exception justifiée. C'est une opportunité majeure pour les PME.
            </p>

            <Card className="bg-green-50 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-green-900 mb-3">Exemple concret</h3>
                <p className="text-green-800 mb-4">
                  Un marché de construction d'école de 5 M€ sera découpé en :
                </p>
                <ul className="space-y-2 text-green-800">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Lot 1 : Gros œuvre (2 M€)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Lot 2 : Électricité (500 k€) ← <strong>Accessible aux PME</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Lot 3 : Plomberie (400 k€) ← <strong>Accessible aux PME</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Lot 4 : Menuiserie (300 k€) ← <strong>Accessible aux PME</strong>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <p className="text-slate-600">
              <strong>Conseil :</strong> Filtrez vos recherches par montant pour cibler les lots à votre portée.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Stratégie 2 : La sous-traitance et le groupement
            </h2>

            <div className="space-y-6">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-blue-600" />
                    La sous-traitance
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Les grandes entreprises ont souvent besoin de sous-traitants locaux. Identifiez les 
                    titulaires de gros marchés dans votre région et proposez vos services.
                  </p>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Inscrivez-vous sur les plateformes de sous-traitance</li>
                    <li>• Contactez directement les majors du BTP/services</li>
                    <li>• Valorisez votre connaissance du terrain local</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-violet-500">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Le groupement d'entreprises
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Associez-vous avec d'autres PME pour répondre ensemble à des marchés plus importants.
                  </p>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• <strong>Groupement conjoint :</strong> Chacun répond de sa partie</li>
                    <li>• <strong>Groupement solidaire :</strong> Responsabilité partagée</li>
                    <li>• Combinez des compétences complémentaires</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Stratégie 3 : Valoriser vos atouts RSE
            </h2>

            <p className="text-slate-600 mb-6">
              Les critères <strong>RSE (Responsabilité Sociétale des Entreprises)</strong> prennent de plus 
              en plus de poids dans les marchés publics. Les PME locales ont des avantages naturels :
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Leaf className="w-10 h-10 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-2">Empreinte carbone</h4>
                  <p className="text-sm text-slate-600">Moins de transport = moins d'émissions CO2</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-2">Emploi local</h4>
                  <p className="text-sm text-slate-600">Vous créez des emplois sur le territoire</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Handshake className="w-10 h-10 text-violet-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-2">Circuit court</h4>
                  <p className="text-sm text-slate-600">Économie locale et réactivité</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-violet-50 border-violet-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-violet-900 mb-3">Comment valoriser ces atouts ?</h3>
                <ul className="space-y-2 text-violet-800">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                    <span>Mentionnez votre implantation locale dans le mémoire technique</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                    <span>Calculez les km évités par rapport à un concurrent national</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                    <span>Obtenez des labels RSE (Lucie, B Corp, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                    <span>Mettez en avant vos engagements sociaux et environnementaux</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Stratégie 4 : Cibler les MAPA
            </h2>

            <p className="text-slate-600 mb-6">
              Les <strong>Marchés à Procédure Adaptée (MAPA)</strong> sont idéaux pour les PME :
            </p>

            <ul className="space-y-4 mb-8">
              {[
                { title: 'Procédure simplifiée', desc: 'Moins de formalisme, délais plus courts' },
                { title: 'Montants accessibles', desc: 'Généralement inférieurs à 221 000 € pour les collectivités' },
                { title: 'Proximité favorisée', desc: 'Les acheteurs locaux cherchent souvent des prestataires proches' },
                { title: 'Négociation possible', desc: 'Contrairement aux appels d\'offres formalisés' },
              ].map((item, i) => (
                <li key={`item-${i}`} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">{item.title}</span>
                    <span className="text-slate-600"> — {item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>

            <p className="text-slate-600">
              <strong>Astuce :</strong> Les marchés de moins de 40 000 € ne sont pas publiés. 
              Prospectez directement auprès des mairies, communautés de communes et établissements publics locaux.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Les erreurs à éviter
            </h2>

            <div className="space-y-4">
              {[
                { error: 'Se décourager face à la complexité', solution: 'Commencez par des petits marchés pour apprendre' },
                { error: 'Viser trop grand d\'emblée', solution: 'Construisez progressivement vos références' },
                { error: 'Négliger le mémoire technique', solution: 'C\'est souvent plus important que le prix' },
                { error: 'Répondre à tout', solution: 'Sélectionnez les marchés où vous avez de vraies chances' },
                { error: 'Sous-estimer les délais', solution: 'Préparez vos offres avec une marge de sécurité' },
              ].map((item, i) => (
                <Card key={`item-${i}`} className="border-l-4 border-l-red-400">
                  <CardContent className="p-4">
                    <p className="font-semibold text-red-900 mb-1">❌ {item.error}</p>
                    <p className="text-slate-600 text-sm">→ {item.solution}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-600 mb-4">
              Les <strong>marchés publics</strong> représentent une opportunité majeure pour les PME. 
              Avec la bonne stratégie — cibler les lots adaptés, valoriser vos atouts locaux, 
              vous associer si nécessaire — vous pouvez rivaliser avec les grandes entreprises.
            </p>
            <p className="text-slate-600">
              La clé du succès ? <strong>Trouver les bons marchés</strong> au bon moment. C'est exactement 
              ce que fait Mon DCE Express : notre IA analyse votre profil et vous recommande les appels 
              d'offres où vous avez les meilleures chances de réussite.
            </p>
          </section>

        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez les marchés faits pour votre PME
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Notre IA identifie les appels d'offres adaptés à votre taille et votre expertise.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
